import useMainFilter from "src/hooks/useMainFilter";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import useCurrentAccount from "src/hooks/useCurrentAccount";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import PageTitle from "src/components/PageTitle/PageTitle";
import { useNavigate, useParams } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  MdClose,
  MdCode,
  MdDelete,
  MdDownload,
  MdEdit,
  MdFilterAlt,
  MdFolderZip,
  MdImage,
  MdInfoOutline,
  MdMoreHoriz,
  MdOutlineArrowForward,
  MdOutlineAttachment,
  MdOutlineFolder,
  MdOutlineFolderOpen,
  MdOutlineImage,
  MdPictureAsPdf,
} from "react-icons/md";
import {
  BUTTON_STYLES,
  ETransactionType,
  ICON_SIZES,
} from "src/helpers/constants";
import PageSubtitleAccount from "src/components/PageTitle/PageSubtitleAccount";
import useMobile from "src/hooks/useMobile";
import ButtonGroup from "src/components/ButtonGroup/ButtonGroup";
import useGeneralOptionsDropdownMenu from "src/hooks/useGeneralOptionsDropdownMenu";
import useAccountsDropdownMenu from "src/hooks/useAccountsDropdownMenu";
import TodayTotalsBar from "src/components/TodayTotalsBar/TodayTotalsBar";
import CurrentAccountCard from "src/components/CurrentAccountCard/CurrentAccountCard";
import useCollapsibles, {
  EAttachmentsPageMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import AccountNotFound from "src/components/AccountNotFound/AccountNotFound";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import useModalsStore from "src/store/modalsStore";
import useFilteredTransactions from "src/hooks/useFilteredTransactions";
import CommonTable from "src/components/Tables/CommonTable";
import {
  formatCurrency,
  formatEasyDate,
  getFileInfo,
  getTotalByTransactionType,
  showNotification,
} from "src/helpers/utils";
import ActiveMainFilterMessage from "src/components/ActiveMainFilterMessage/ActiveMainFilterMessage";
import TransactionAttachmentsLightbox from "../NewTransaction/TransactionAttachmentsLightbox";
import useHistoryStore from "src/store/historyStore";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import moment from "moment";
import { uniqBy, countBy, sortBy, toPairs, orderBy } from "lodash-es";
import Modal from "src/components/Modal/Modal";
import useCharts from "src/hooks/useCharts";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import InputDialog from "src/components/InputDialog/InputDialog";
import usePinnedColumns from "src/hooks/usePinnedColumns";
import ScrollToSection from "src/components/ScrollToSection/ScrollToSection";
import { ICellRendererParams } from "ag-grid-community";
import SlideDownUpAnimation from "src/components/Animations/SlideDownUpAnimation";
import NoTableData from "src/components/NoTableData/NoTableData";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";
import { EAttachmentsTableKeys } from "src/components/Tables/tableIds";

const AttachmentsPage: React.FC = () => {
  const { isMobile } = useMobile();
  const [canScroll, setCanScroll] = useState(false);

  const { chartPreferences } = useCharts();
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);

  const { setShowSearchModal } = useModalsStore();
  const navigate = useNavigate();
  const [clickedAttachmentIndex, setClickedAttachmentIndex] =
    useState<number>(0);
  const [attachmentsGalleryIsOpen, setAttachmentsGalleryIsOpen] =
    useState<boolean>(false);
  const thumbnailsRef = useRef<any>(null);

  const { isTransactionInHistory, markTransactionAsOpened, historyIsEnabled } =
    useHistoryStore();
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

  const {
    isFilteringTransactions,
    filteredTransactions,
    getFilteredTransactionById,
  } = useFilteredTransactions();

  const { t } = useTranslation();
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();
  const accountsGlobalMenu = useAccountsDropdownMenu({
    linkTo: "attachments",
  });
  const {
    collapsiblePreferences,
    updateCollapsible,
    isAttachmentsPageMobileCollapsibleOpen,
    toggleAttachmentsPageMobileCollapsible,
  } = useCollapsibles();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const { isPinnedColumn, updatePinnedColumns } = usePinnedColumns();

  const { setMainFilter, mainFilter, isMainFilterActive } = useMainFilter();
  const { account: accountParameter } = useParams();

  useEffect(() => {
    setMainFilter({ account: accountParameter });
  }, [accountParameter]);

  const {
    isLoadingCurrentAccount,
    currentAccountData,
    currencySymbol,
    activeTransactions,
  } = useCurrentAccount();

  const getAvailableMoney = useMemo(() => {
    const income = getTotalByTransactionType({
      type: ETransactionType.INCOME,
      transactions: activeTransactions,
    });
    const expenses = getTotalByTransactionType({
      type: ETransactionType.EXPENSE,
      transactions: activeTransactions,
    });

    return income - expenses;
  }, [activeTransactions]);

  const isLoadingSomething = useMemo(
    () =>
      [!!isLoadingCurrentAccount, !!isFilteringTransactions]?.includes(true),
    [isLoadingCurrentAccount, isFilteringTransactions]
  );

  const attachmentsList = useMemo(() => {
    if (!filteredTransactions?.length) return [];

    let index = 0;

    const result = filteredTransactions.flatMap((transaction: any) => {
      const attachments = transaction?.attachments ?? [];

      if (!attachments.length) return [];

      const transactionInfo = {
        invoice: transaction.invoice,
        account: transaction.account,
        date: transaction.date || "Desconocida",
        formattedDate: formatEasyDate({ dateToFormat: transaction.date }),
      };

      return attachments.map((file: any) => {
        const info = getFileInfo(file);

        return {
          fakeIndex: index++, // 🔢 contador global
          ...info,
          ...transactionInfo,
          src: info.fileUrl,
          thumbnail: info.thumbnailUrl,
          type: ["XML", "PDF"].includes(info.formattedType)
            ? undefined
            : "image",
        };
      });
    });

    return result;
  }, [filteredTransactions]);

  const downloadFile = () => {
    if (selectedRows?.length === 1 && clickedAttachmentData) {
      const link = document.createElement("a");
      link.href = clickedAttachmentData?.fileUrl;
      link.download = clickedAttachmentData?.originalFileName;
      link.style.display = "none";
      link.click();
    }
  };

  const clickedAttachmentData = useMemo(() => {
    if (selectedRows?.length === 1) {
      return attachmentsList[selectedRows?.[0]?.fakeIndex];
    }

    return null;
  }, [clickedAttachmentIndex, attachmentsList, selectedRows]);

  const selectedRowsHaveSameTransaction = useMemo(() => {
    if (selectedRows?.length) {
      const uniqueTransactions = uniqBy(selectedRows, "invoice");

      return uniqueTransactions?.length === 1;
    }

    return null;
  }, [selectedRows]);

  const transactionData = useMemo(() => {
    if (selectedRows.length === 1 || selectedRowsHaveSameTransaction) {
      const [firstTransaction] = selectedRows;

      return getFilteredTransactionById({
        transactionId: firstTransaction.invoice,
      });
    }

    return null;
  }, [clickedAttachmentIndex, attachmentsList, selectedRows]);

  useEffect(() => {
    setClickedAttachmentIndex(0);
    setSelectedRows([]);
  }, [mainFilter?.account]);

  useEffect(() => {
    if (!isMobile) {
      setShowInfoModal(false);
    }
  }, [isMobile]);

  const fallbackColors = [
    "#e74c3c",
    "#2ecc71",
    "#3498db",
    "#f39c12",
    "#9b59b6",
    "#1abc9c",
    "#e67e22",
    "#34495e",
    "#7f8c8d",
    "#d35400",
  ];

  const groupedCount = useMemo(() => {
    return countBy(attachmentsList, (f: any) => f.formattedType.toUpperCase());
  }, [attachmentsList]);

  const sortedEntries = useMemo(
    () => sortBy(toPairs(groupedCount), ([key]) => key),
    [groupedCount]
  );

  const categories = useMemo(
    () => sortedEntries.map(([type]) => type),
    [sortedEntries]
  );

  const categoryColorMap = useMemo(() => {
    return categories.reduce((acc, type, index) => {
      acc[type] = fallbackColors[index % fallbackColors.length];
      return acc;
    }, {} as Record<string, string>);
  }, [categories]);

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "NOMBRE",
        flex: 1,
        minWidth: 200,
        pinned: isPinnedColumn("attachmentsNamePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                attachmentsNamePinned: isPinned,
              });
            },
          },
        },
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div
              className={clsx([
                "inline-flex",
                "items-center",
                "justify-start",
                "h-full",
                "gap-1",
                "cursor-pointer",
                "hover:underline",
              ])}
              onClick={() => {
                const { data } = params;

                setClickedAttachmentIndex(data.fakeIndex);
                setAttachmentsGalleryIsOpen(true);
              }}
            >
              {params?.value}
            </div>
          );
        },
      },

      {
        field: "formattedSize",
        headerName: "TAMAÑO",
        pinned: isPinnedColumn("attachmentsSizePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                attachmentsSizePinned: isPinned,
              });
            },
          },
        },
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div
              className={clsx([
                "inline-flex",
                "items-center",
                "justify-start",
                "h-full",
                "gap-1",
                "cursor-pointer",
                "hover:underline",
              ])}
              onClick={() => {
                const { data } = params;

                setClickedAttachmentIndex(data.fakeIndex);
                setAttachmentsGalleryIsOpen(true);
              }}
            >
              {params?.value}
            </div>
          );
        },
      },
      {
        field: "formattedType",
        headerName: "TIPO",
        pinned: isPinnedColumn("attachmentsTypePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                attachmentsTypePinned: isPinned,
              });
            },
          },
        },
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: categoryColorMap[params?.value],
              color: "#fff",
              className: "text-right",
              onClick: () => {
                const { data } = params;

                setClickedAttachmentIndex(data.fakeIndex);
                setAttachmentsGalleryIsOpen(true);
              },
            },
          };
        },
      },
      {
        field: "formattedDate",
        headerName: "FECHA",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: "#555",
              color: "#fff",
              className: "text-right",
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
        pinned: isPinnedColumn("attachmentsDatePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                attachmentsDatePinned: isPinned,
              });
            },
          },
        },
      },
      {
        cellRenderer: "CellWithButtonsRenderer",
        headerName: "ACCIONES",
        hide: true,
        sortable: false,
        // minWidth: 80,
        pinned: isPinnedColumn("attachmentsActionsPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                attachmentsActionsPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: any) => {
          const alreadyOpened =
            historyIsEnabled && isTransactionInHistory(params.data.invoice);
          return {
            buttons: [
              {
                icon: <MdInfoOutline size={ICON_SIZES.SM} />,
                onClick: () => {
                  const { data } = params;
                  setClickedAttachmentIndex(data.fakeIndex);
                  setShowInfoModal(true);
                },
                className: clsx(["grid-button"]),
                hide: true,
              },
              {
                icon:
                  params?.data?.type === "image" ? (
                    <MdOutlineImage size={ICON_SIZES.SM} />
                  ) : params?.data?.formattedType === "XML" ? (
                    <MdCode size={ICON_SIZES.SM} />
                  ) : (
                    <MdPictureAsPdf size={ICON_SIZES.SM} />
                  ),
                onClick: () => {
                  const { data } = params;
                  setClickedAttachmentIndex(data.fakeIndex);
                  setAttachmentsGalleryIsOpen(true);
                },
                className: clsx(["grid-button"]),
              },
              {
                icon: alreadyOpened ? (
                  <MdOutlineFolderOpen size={ICON_SIZES.SM} />
                ) : (
                  <MdOutlineFolder size={ICON_SIZES.SM} />
                ),

                onClick: (e: any) => {
                  e?.stopPropagation?.();
                  const { data } = params;

                  if (
                    historyIsEnabled &&
                    !isTransactionInHistory(data.invoice)
                  ) {
                    markTransactionAsOpened(data.invoice);
                  }

                  navigate(`/transaction/${data?.account}/${data?.invoice}`);
                },
                className: clsx(["grid-button"]),
              },
            ],
          };
        },
      },
    ];
  }, [isMobile, categoryColorMap, sortedEntries]);

  const seriesData = useMemo(
    () => [
      {
        name: "Archivos",
        type: chartPreferences.type,
        data: sortedEntries.map(([type, value]) => ({
          value,
          itemStyle: {
            color: categoryColorMap[type] || "#ccc",
            borderRadius: [6, 6, 0, 0], // [topLeft, topRight, bottomRight, bottomLeft]
          },
        })),
        large: true,
      },
    ],
    [sortedEntries, categoryColorMap, chartPreferences.type]
  );

  const downloadZip = async () => {
    const zip = new JSZip();

    if (!selectedRows || selectedRows.length === 0) return;

    const fetchFileAsBlob = async (url: string): Promise<Blob> => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Error al descargar archivo: ${url}`);
      return await response.blob();
    };

    for (const attachment of selectedRows) {
      try {
        const blob = await fetchFileAsBlob(attachment.fileUrl);
        zip.file(attachment.name, blob);
      } catch (error) {
        showNotification({
          kind: "error",
          message: `Error agregando ${attachment?.name} al ZIP`,
        });
      }
    }

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "adjuntos.zip");
  };

  const menuOptions = useMemo(() => {
    return orderBy(
      [
        {
          text: t("Renombrar"),
          onClick: () => setShowRenameDialog(true),
          icon: <MdEdit size={ICON_SIZES.MD} />,
          disabled: selectedRows?.length !== 1,
          hide: selectedRows?.length > 1,
        },
        {
          text: t("Descargar"),
          onClick: () => {},
          icon: <MdDownload size={ICON_SIZES.MD} />,
          disabled: selectedRows?.length !== 1,
          hide: selectedRows?.length > 1,
        },
        {
          text: t("Descargar ZIP"),
          onClick: () => downloadZip(),
          icon: <MdFolderZip size={ICON_SIZES.MD} />,
          disabled: selectedRows?.length < 1,
          hide: selectedRows?.length === 1,
        },
        {
          text: t("Eliminar permanentemente"),
          onClick: () => setShowDeleteDialog(true),
          icon: <MdDelete size={ICON_SIZES.MD} />,
          disabled: !selectedRows?.length,
        },
      ],
      ["text"],
      ["asc"]
    );
  }, [selectedRows]);

  return (
    <>
      <TwoSidebars
        isLoading={isLoadingSomething}
        right={
          isLoadingSomething ||
          !mainFilter?.account ||
          !currentAccountData ||
          currentAccountData?.settings?.disabled ? null : isMobile ? null : (
            <>
              <CollapsibleBlock
                title={t("Cuenta")}
                uppercase
                open={collapsiblePreferences.accountOpen}
                onChange={(isOpen: boolean) =>
                  updateCollapsible({ accountOpen: isOpen })
                }
                badge={formatCurrency({
                  value: getAvailableMoney,
                  currency: currencySymbol,
                })}
              >
                <CurrentAccountCard />
              </CollapsibleBlock>
              <CollapsibleBlock
                title={t(selectedRows?.length > 1 ? "Archivos" : "Archivo")}
                uppercase
                open={
                  isMobile
                    ? isAttachmentsPageMobileCollapsibleOpen(
                        EAttachmentsPageMobileCollapsibles.FILE_INFO
                      )
                    : collapsiblePreferences.fileInfoOpen
                }
                onChange={(isOpen: boolean) => {
                  if (isMobile) {
                    toggleAttachmentsPageMobileCollapsible(
                      EAttachmentsPageMobileCollapsibles.FILE_INFO
                    );
                  } else {
                    updateCollapsible({ fileInfoOpen: isOpen });
                  }
                }}
                buttons={
                  selectedRows?.length > 0 && !isMobile
                    ? [
                        {
                          icon: <MdDelete size={ICON_SIZES.MD} />,
                          tooltip: "Eliminar permanentemente",
                          onClick: () => setShowDeleteDialog(true),
                        },
                        {
                          text: t("Descargar"),
                          onClick: () => {
                            downloadFile();
                          },
                          icon: <MdDownload size={ICON_SIZES.MD} />,
                          disabled: selectedRows?.length !== 1,
                          hide: selectedRows?.length > 1,
                        },
                        {
                          text: t("Descargar ZIP"),
                          onClick: () => downloadZip(),
                          icon: <MdFolderZip size={ICON_SIZES.MD} />,
                          disabled: selectedRows?.length < 1,
                          hide: selectedRows?.length === 1,
                        },
                        {
                          icon: <MdEdit size={ICON_SIZES.MD} />,
                          tooltip: "Renombrar",
                          onClick: () => setShowRenameDialog(true),
                          hide: selectedRows?.length > 1,
                        },
                      ]
                    : []
                }
              >
                <div
                  className={clsx([
                    "flex",
                    "flex-col",
                    "gap-2",
                    "rounded-md",
                    "overflow-hidden",
                    "bg-white dark:bg-gray-900",
                    "p-4",
                  ])}
                >
                  {selectedRows?.length === 1 ? (
                    <>
                      {[
                        {
                          text: "Nombre",
                          value: clickedAttachmentData?.name,
                        },
                        {
                          text: "Tipo",
                          value: clickedAttachmentData?.formattedType,
                        },
                        {
                          text: "Tamaño",
                          value: clickedAttachmentData?.formattedSize,
                        },
                      ].map?.((data: any, index: number) => (
                        <div
                          className={clsx([
                            "flex",
                            "w-full",
                            "p-0",
                            "text-sm",
                            "flex-row",
                            "rounded-md",
                            "bg-white dark:bg-gray-900",
                            "gap-2",
                          ])}
                          key={index}
                        >
                          <div className={clsx(["font-medium"])}>
                            {data.text}:
                          </div>
                          <div className="break-all">{data.value}</div>
                        </div>
                      ))}
                      {!!clickedAttachmentData?.fileUrl && (
                        <div
                          className={clsx([
                            "flex",
                            "w-full",
                            "p-0",
                            "text-sm",
                            "flex-col",
                            "rounded-md",
                            "bg-white dark:bg-gray-900",
                            "gap-1",
                          ])}
                        >
                          {!["XML", "PDF"].includes(
                            clickedAttachmentData.formattedType
                          ) ? (
                            <img
                              src={clickedAttachmentData?.fileUrl}
                              alt={clickedAttachmentData?.name}
                              className={clsx([
                                "flex",
                                "w-full",
                                "max-w-full",
                                "cursor-pointer",
                                "mx-auto",
                                "rounded-md",
                                "overflow-auto",
                                // "border",
                              ])}
                              onClick={() => {
                                setClickedAttachmentIndex(
                                  clickedAttachmentData.fakeIndex
                                );
                                setAttachmentsGalleryIsOpen(true);
                              }}
                            />
                          ) : clickedAttachmentData.formattedType == "XML" ? (
                            <MdCode
                              size={ICON_SIZES.XL * 2}
                              className="mx-auto opacity-25 cursor-pointer"
                              onClick={() => {
                                setClickedAttachmentIndex(
                                  clickedAttachmentData.fakeIndex
                                );
                                setAttachmentsGalleryIsOpen(true);
                              }}
                            />
                          ) : (
                            <MdPictureAsPdf
                              size={ICON_SIZES.XL * 2}
                              className="mx-auto opacity-25 cursor-pointer"
                              onClick={() => {
                                setClickedAttachmentIndex(
                                  clickedAttachmentData.fakeIndex
                                );
                                setAttachmentsGalleryIsOpen(true);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-sm flex w-full">
                        {!selectedRows?.length ? (
                          "Selecciona un archivo de la lista para ver sus detalles."
                        ) : (
                          <div
                            className={clsx([
                              "flex",
                              "w-full",
                              "flex-col",
                              "gap-2",
                            ])}
                          >
                            <div>
                              Tienes más de un archivo seleccionado. Para ver
                              los detalles debes seleccionar sólo un archivo de
                              la lista.
                            </div>
                            <div>
                              Puedes eliminar o descargar en un ZIP los archivos
                              que ya tienes seleccionados.
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </CollapsibleBlock>
              <CollapsibleBlock
                title="Transacción"
                open={
                  isMobile
                    ? isAttachmentsPageMobileCollapsibleOpen(
                        EAttachmentsPageMobileCollapsibles.TRANSACTION
                      )
                    : collapsiblePreferences.transactionInfoOpen
                }
                onChange={(isOpen: boolean) => {
                  if (isMobile) {
                    toggleAttachmentsPageMobileCollapsible(
                      EAttachmentsPageMobileCollapsibles.TRANSACTION
                    );
                  } else {
                    updateCollapsible({ transactionInfoOpen: isOpen });
                  }
                }}
                buttons={
                  selectedRows?.length &&
                  selectedRowsHaveSameTransaction &&
                  !isMobile
                    ? [
                        {
                          text: "Transacción",
                          onClick: () => {
                            const [firstRow] = selectedRows;

                            if (
                              historyIsEnabled &&
                              !isTransactionInHistory(firstRow?.invoice)
                            ) {
                              markTransactionAsOpened(firstRow?.invoice);
                            }
                            navigate(
                              `/transaction/${firstRow?.account}/${firstRow?.invoice}`
                            );
                          },
                          icon:
                            historyIsEnabled &&
                            isTransactionInHistory(
                              selectedRows?.[0]?.invoice
                            ) ? (
                              <MdOutlineArrowForward size={ICON_SIZES.MD} />
                            ) : (
                              <MdOutlineArrowForward size={ICON_SIZES.MD} />
                            ),
                        },
                      ]
                    : []
                }
              >
                <div
                  className={clsx([
                    "flex",
                    "flex-col",
                    "gap-2",
                    "rounded-md",
                    "overflow-hidden",
                    "bg-white dark:bg-gray-900",
                    "p-4",
                  ])}
                >
                  {selectedRows?.length && selectedRowsHaveSameTransaction ? (
                    <>
                      {[
                        {
                          text: "Fecha",
                          value: moment(transactionData?.date).format(
                            "dddd DD [de] MMMM, YYYY"
                          ),
                        },
                        {
                          text: "Origen / Destino",
                          value: transactionData?.payTo,
                        },
                        ...(!transactionData?.description
                          ? []
                          : [
                              {
                                text: "Descripción",
                                value: transactionData?.description,
                              },
                            ]),
                        ...(!transactionData?.details?.length
                          ? []
                          : [
                              {
                                text: "Filas",
                                value: transactionData?.details?.length,
                              },
                            ]),
                        {
                          text: "Total",
                          value: formatCurrency({
                            currency: currencySymbol,
                            value: transactionData?.total,
                          }),
                        },
                        {
                          text: "Categoría",
                          value: transactionData?.category || "Ninguna",
                        },
                        {
                          text: "Total",
                          value: formatCurrency({
                            currency: currencySymbol,
                            value: transactionData?.total,
                          }),
                        },
                        ...(!transactionData?.notes
                          ? []
                          : [
                              {
                                text: "Notas",
                                value: transactionData?.notes,
                              },
                            ]),
                        {
                          text: "Archivos",
                          value: transactionData?.attachments?.length,
                        },
                      ].map?.((data: any, index: number) => (
                        <div
                          className={clsx([
                            "flex",
                            "w-full",
                            "p-0",
                            "text-sm",
                            "flex-row",
                            "gap-2",
                            // "rounded-md",
                            "bg-white dark:bg-gray-900",
                          ])}
                          key={index}
                        >
                          <div className={clsx(["font-medium"])}>
                            {data.text}:
                          </div>
                          <div className="break-all first-letter:capitalize">
                            {data.value}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div className="text-sm flex w-full">
                        {!selectedRows?.length
                          ? "Selecciona uno o varios archivos de la lista para ver los detalles de la transacción."
                          : "Los archivos seleccionados son de diferentes transacciones. Selecciona sólamente un archivo o asegúrate de que todos pertenezcan a la misma transacción."}
                      </div>
                    </>
                  )}
                </div>
              </CollapsibleBlock>
            </>
          )
        }
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "w-full",
            "gap-2",
            "md:gap-2",
            "h-full",
          ])}
        >
          <AnimatePresence>
            {mainFilter?.account ? (
              <>
                {isLoadingSomething ? (
                  <LoadingWithTextPage />
                ) : (
                  <>
                    {currentAccountData &&
                    !currentAccountData?.settings?.disabled ? (
                      <>
                        <PageTitle
                          icon={<MdOutlineAttachment size={ICON_SIZES.MD} />}
                          title={t("Archivos")}
                          subtitle={
                            isMobile && accountParameter ? (
                              <PageSubtitleAccount {...currentAccountData} />
                            ) : null
                          }
                          rightSide={
                            <>
                              <ButtonGroup
                                buttons={[
                                  {
                                    icon: <MdMoreHoriz size={ICON_SIZES.MD} />,
                                    tooltip: t("Más opciones"),
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                    options: menuOptions,
                                  },
                                  {
                                    icon: isMainFilterActive ? (
                                      <MdFilterAlt
                                        className="text-red-500 dark:text-purple-500"
                                        size={ICON_SIZES.MD}
                                      />
                                    ) : (
                                      <MdFilterAlt size={ICON_SIZES.MD} />
                                    ),
                                    onClick: () => {
                                      setShowSearchModal(true);
                                    },
                                    tooltip: t("transactions_search.title"),
                                    // hide: !isMobile,
                                    className: isMobile
                                      ? BUTTON_STYLES.HEADING
                                      : "",
                                  },
                                  accountsGlobalMenu,
                                  generalOptionsDropdown,
                                ]}
                                gap={1}
                              />
                            </>
                          }
                          isLoading={isLoadingCurrentAccount}
                        />

                        <TodayTotalsBar />

                        <SlideDownUpAnimation
                          show={
                            !!(isMobile && collapsiblePreferences.accountOpen)
                          }
                        >
                          <CurrentAccountCard />
                        </SlideDownUpAnimation>

                        <div
                          className={clsx([
                            "flex",
                            "flex-col",
                            "gap-4",
                            "w-full",
                          ])}
                          id="focused-section"
                        >
                          <ActiveMainFilterMessage
                            onShowSearch={() => setShowSearchModal(true)}
                          />

                          <motion.div
                            className={clsx([
                              "w-full",
                              "flex",
                              "flex-col",
                              "gap-6",
                            ])}
                            {...(isMobile
                              ? {
                                  initial: {
                                    opacity: 0.99,
                                  },
                                  animate: { opacity: 1 },
                                  transition: { duration: 0.1 },
                                }
                              : {
                                  initial: { opacity: 0 },
                                  animate: { opacity: 1 },
                                  exit: { opacity: 0 },
                                  transition: { duration: 2, ease: "easeOut" },
                                })}
                            onAnimationComplete={() => {
                              if (
                                !!(
                                  isMobile && collapsiblePreferences.accountOpen
                                )
                              ) {
                                setCanScroll(true);
                              }
                            }}
                          >
                            <CollapsibleBlock
                              title="Formatos"
                              open={collapsiblePreferences.attachmentsChartOpen}
                              onChange={(isOpen: boolean) => {
                                updateCollapsible({
                                  attachmentsChartOpen: isOpen,
                                });
                              }}
                              uppercase
                            >
                              <div
                                className={clsx([
                                  "flex",
                                  "w-full",
                                  "gap-4",
                                  "flex-col",
                                ])}
                              >
                                {sortedEntries?.length ? (
                                  <>
                                    {sortedEntries?.length > 0 && (
                                      <div
                                        className={clsx([
                                          "grid",
                                          "w-full",
                                          "gap-2",
                                          // "hidden",
                                          "grid-cols-[repeat(auto-fit,_minmax(100px,_1fr))] ",
                                        ])}
                                      >
                                        {sortedEntries?.map?.(
                                          ([type, value], key: number) => (
                                            <motion.div
                                              key={type}
                                              className={clsx([
                                                "flex",
                                                "flex-col",
                                                "px-2",
                                                "py-2",
                                                "rounded-md",
                                                // "border",
                                                "text-sm",
                                                "justify-center",
                                                "items-center",
                                                "border-gray-200",
                                                "dark:border-gray-600",
                                                "text-white",
                                              ])}
                                              style={{
                                                background:
                                                  categoryColorMap[type],
                                              }}
                                              initial={{
                                                opacity: 0,
                                                // x: key % 2 === 0 ? -100 : 100,
                                              }}
                                              animate={{ opacity: 1 }}
                                              transition={{
                                                duration: 0.2,
                                                ease: "easeOut",
                                                delay: key * 0.1,
                                              }}
                                            >
                                              <div>
                                                {[
                                                  "JPEG",
                                                  "JPG",
                                                  "PNG",
                                                ].includes(type) && (
                                                  <MdImage
                                                    size={ICON_SIZES.LG}
                                                  />
                                                )}
                                                {type === "PDF" && (
                                                  <MdPictureAsPdf
                                                    size={ICON_SIZES.LG}
                                                  />
                                                )}
                                                {type === "XML" && (
                                                  <MdCode
                                                    size={ICON_SIZES.LG}
                                                  />
                                                )}
                                              </div>
                                              <div
                                                className={clsx([
                                                  "md:text-base",
                                                  "text-sm",
                                                ])}
                                              >
                                                {value}
                                              </div>
                                              <div
                                                className={clsx([
                                                  "font-medium",
                                                  "text-sm",
                                                  "md:text-base",
                                                ])}
                                              >
                                                {type}
                                              </div>
                                            </motion.div>
                                          )
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <NoTableData
                                    className={clsx(["!mt-10", "md:!mt-16"])}
                                  />
                                )}
                              </div>
                            </CollapsibleBlock>

                            <CollapsibleBlock
                              title="Tabla"
                              open={collapsiblePreferences.attachmentsTableOpen}
                              onChange={(isOpen: boolean) => {
                                updateCollapsible({
                                  attachmentsTableOpen: isOpen,
                                });
                              }}
                              uppercase
                            >
                              <CommonTable
                                onSelectionChange={setSelectedRows}
                                columns={columns}
                                data={attachmentsList}
                                settingsKey={
                                  EAttachmentsTableKeys.ATTACHMENTS_LIST
                                }
                              />
                            </CollapsibleBlock>
                          </motion.div>
                        </div>
                        <ScrollToSection
                          targetId="focused-section"
                          enabled={canScroll}
                        />
                      </>
                    ) : (
                      <AccountNotFound
                        exists={!!currentAccountData}
                        disabled={!!currentAccountData?.settings?.disabled}
                        linkTo="attachments"
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <AccountNotFound linkTo="attachments" />
            )}
          </AnimatePresence>
        </div>
      </TwoSidebars>

      {attachmentsGalleryIsOpen && (
        <TransactionAttachmentsLightbox
          slides={attachmentsList}
          index={clickedAttachmentIndex}
          setIndex={setClickedAttachmentIndex}
          unifiedAttachmentsList={attachmentsList}
          onClose={() => setAttachmentsGalleryIsOpen(false)}
          thumbnailsRef={thumbnailsRef}
        />
      )}

      {showInfoModal && (
        <Modal
          onEscape={() => setShowInfoModal(false)}
          className={clsx(["!w-full"])}
        >
          <Modal.Header
            title="Información del archivo"
            onClose={() => setShowInfoModal(false)}
          />
          <Modal.Body>
            <div
              className={clsx([
                "flex",
                "w-full",
                "flex-col",
                "gap-4",
                "md:gap-6",
                "text-sm",
                "md:text-base",
                "pb-2",
              ])}
            >
              {/* <InfoBlocks /> */}
            </div>
          </Modal.Body>
          <Modal.Footer
            actions={[
              {
                text: "Transacción",
                onClick: () => {
                  const [firstRow] = selectedRows;

                  if (
                    historyIsEnabled &&
                    !isTransactionInHistory(firstRow?.invoice)
                  ) {
                    markTransactionAsOpened(firstRow?.invoice);
                  }
                  navigate(
                    `/transaction/${firstRow?.account}/${firstRow?.invoice}`
                  );
                },
                icon:
                  historyIsEnabled &&
                  isTransactionInHistory(selectedRows?.[0]?.invoice) ? (
                    <MdOutlineFolderOpen size={ICON_SIZES.SM} />
                  ) : (
                    <MdOutlineFolder size={ICON_SIZES.SM} />
                  ),
                hide:
                  selectedRows?.length === 0 ||
                  (selectedRows?.length > 1 &&
                    !selectedRowsHaveSameTransaction),
              },
              {
                text: t("shared.close"),
                onClick: () => setShowInfoModal(false),
                kind: EButtonKind.NORMAL,
                icon: <MdClose size={ICON_SIZES.MD} />,
              },
            ]}
          />
        </Modal>
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          description={t("dialogs.delete_transactions.delete_question", {
            count: selectedRows?.length,
          })}
          note={t("dialogs.delete_transactions.delete_question_note", {
            count: selectedRows?.length,
          })}
          onClose={() => setShowDeleteDialog(false)}
          doubleConfirmRequired
          onConfirm={async () => {}}
          title={t("dialogs.delete_transactions.title")}
          confirmKind={EButtonKind.DANGER}
          isLoading={false}
        />
      )}
      {showRenameDialog && (
        <InputDialog
          type="text"
          description="Nuevo nombre:"
          title="Renombrar"
          isLoading={false}
          onClose={() => setShowRenameDialog(false)}
          onConfirm={async (answer: string) => {}}
        />
      )}
    </>
  );
};

export default AttachmentsPage;
